import type { FC } from 'react';
import { KeywordsWrapper } from './Keywords.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface KeywordsProps { }

const Keywords: FC<KeywordsProps> = () => (
   <KeywordsWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Static, this, super &amp; final keyword</h1>
      </header>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <br />
         <h4 className="text-align-justify"><strong><em>static keyword</em></strong></h4>
         <ul className="text-align-justify">
            <li>In order to share the same method or variable of any given class, we use the static keyword.</li>
            <li>We can use it for blocks, variables, methods, and nested classes.</li>
            <li><strong>Static variable </strong>
               <ul>
                  <li>A static variable is nothing but the class variable.</li>
                  <li>It is common for all objects of a class, or let’s say it is shared by all objects of a class, whereas the non-static variable is different for each object of a class.</li>
                  <li>Example
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                     { 
                        'static String name="John";'
                     }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <li><strong>Static block </strong>
               <ul>
                  <li>To initialize static variables, we use a static block.</li>
                  <li>It gets executed only once after class is loaded or an object is created.</li>
                  <li>A class can contain multiple static blocks.</li>
                  <li>Example
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                     {
                        'static String name;\n'+
                        'static {\n'+
                        '  name = "Java";\n'+
                        '}'
                     }   
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <li><strong>Static method </strong>
               <ul>
                  <li>A static method is one that can be invoked without creating object of a class.</li>
                  <li>It can access static variables without using the object of the class.</li>
                  <li>&nbsp;It can access static and non-static methods directly. A non-static method can access the static method, while a static method can access the non-static method only through the object of a class.</li>
                  <li>Example
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'public static void main(String args[]) {}'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <li><strong>Static class </strong>
               <ul>
                  <li>We can declare a class as static only if it is a nested class.</li>
                  <li>Non-static members of the Outer class are not accessible by the static class.</li>
                  <li>Example
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'class Vehicle {\n'+
                           '  //Static class \n'+
                           '  static class Car {}\n'+
                           '}'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
         </ul>
         <br />
         <h4 className="text-align-justify"><strong><em>this keyword</em></strong></h4>
         <ul className="text-align-justify">
            <li>“this” is basically just a reference variable referring to the current object.</li>
            <li>It eliminates the confusion between class attributes and parameters with the same name.&nbsp;</li>
            <li>We can use it to :
               <ul>
                  <li>refer class variable</li>
                  <li>invoke class method or constructor</li>
                  <li>passed as an argument in method or constructor</li>
                  <li>return current class object</li>
               </ul>
            </li>
            <li>Example
               <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                  {
                     'this.name = name;\n'+
                     'this.setName();\n'+
                     'setName(this);\n'+
                     'Person p = new Person(this);\n'+
                     'return this;'
                  }
               </SyntaxHighlighter>
            </li>
         </ul>
         <br />
         <h4 className="text-align-justify"><strong><em>super keyword</em></strong></h4>
         <ul className="text-align-justify">
            <li>The “super” keyword is a reference variable in java, used to refer parent class objects.</li>
            <li>We can use the super keyword in three ways :
               <ul>
                  <li><strong>with variable </strong>
                     <ul>
                        <li>It is used when a derived class and base class has the same data members. There is a possibility of ambiguity in such a case.&nbsp;</li>
                        <li>This keyword helps us refer to the immediate parent class instance variable.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'System.out.println(super.speed);'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <li><strong>with method </strong>
                     <ul>
                        <li>When we want to call the parent class method, and parent &amp; child class have same-named methods; then, to resolve ambiguity, we use the super keyword to invoke the immediate parent class method.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'super.message();'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <li><strong>with constructor </strong>
                     <ul>
                        <li>To invoke the immediate parent class constructor, we can use it with a constructor.</li>
                        <li>It can call both parametric as well as non-parametric constructors depending upon the situation.</li>
                        <li>Call to super() must be the first statement in a derived class constructor.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'Student() {\n'+
                                 '  super();\n'+
                                 '  System.out.println("constructor");\n'+
                                 '}'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
               </ul>
            </li>
         </ul>
         <br />
         <h4 className="text-align-justify"><strong><em>final keyword </em></strong></h4>
         <ul className="text-align-justify">
            <li>To apply restrictions on user access, the final keyword is used.</li>
            <li>There are three ways to use the final keyword :
               <ul>
                  <li><strong>final variable&nbsp;</strong>
                     <ul>
                        <li>Its value cannot be changed once initialized.</li>
                        <li>The compiler cannot assign a default value to a final variable.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'final int count = 10;'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
                  <li><strong>final method</strong>&nbsp;
                     <ul>
                        <li>A final method cannot​ be overridden, nor can it be hidden by a subclass.</li>
                        <li>Since the final method cannot be overridden so core functionalities must be declared.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'final void manageCount() {\n'+
                                 '  //code \n'+
                                 '}'
                              }
                           </SyntaxHighlighter>   
                        </li>
                     </ul>
                  </li>
                  <li><strong>final class</strong>&nbsp;
                     <ul>
                        <li>It cannot be inherited.</li>
                        <li>We cannot declare a final class as abstract at the same time as they are antonyms.</li>
                        <li>Example
                           <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                              {
                                 'final class Car {\n'+
                                 '  //methods and fields \n'+
                                 '}'
                              }
                           </SyntaxHighlighter>
                        </li>
                     </ul>
                  </li>
               </ul>
            </li>
         </ul>
      </div>
   </KeywordsWrapper>
);

export default Keywords;
